import { useTrackerTicket } from '@/queries/tracker'
import { MessageThread } from '@/features/ticket-detail/MessageThread'
import { ReplyComposer } from '@/features/ticket-detail/ReplyComposer'
import { FullPageSpinner } from '@/components/etainabl-spinner'
import { ViewingAsBanner } from '@/features/view-as/ViewingAsBanner'
import { isAdminViewer } from '@/lib/scope'
import { TrackerPanel } from './TrackerPanel'
import { CopyForClaude } from './CopyForClaude'

export function TrackerDetailView({ ticketId, asUser }: { ticketId: string; asUser?: string }) {
  const { data, isLoading, error } = useTrackerTicket(ticketId, asUser)
  if (isLoading) return <FullPageSpinner />

  // A forged asUser on a non-admin session gets an explicit message, not a
  // blank "not found" that reads as a broken link — mirrors TicketDetailView.
  const status = (error as { status?: number; statusCode?: number } | null)?.status
    ?? (error as { statusCode?: number } | null)?.statusCode
  if (status === 403) {
    return (
      <div className="p-6 text-sm text-muted-foreground">
        You don&apos;t have access to view as another user.
      </div>
    )
  }

  if (error || !data) return <div className="p-8">Ticket not found.</div>

  return (
    <div className="flex h-screen -mx-8 -my-6 flex-col">
      {data.viewingAs && <ViewingAsBanner name={data.viewingAs.name} companyName={data.viewingAs.companyName} />}
      <div className="flex flex-1 min-h-0">
        <div className="flex-1 flex flex-col overflow-hidden min-w-0">
          <header className="p-4 border-b flex items-center gap-4 shrink-0">
            <h1 className="text-xl font-semibold flex-1 min-w-0 truncate">{data.ticket.subject}</h1>
            {isAdminViewer(data.viewerRelation) && <CopyForClaude ticketId={ticketId} />}
          </header>
          <div className="flex-1 overflow-y-auto min-h-0">
            <MessageThread ticketId={ticketId} messages={data.messages} />
          </div>
          <div className="shrink-0 border-t bg-background">
            {data.viewingAs ? (
              <div className="p-4 border-t text-sm text-muted-foreground">
                This is a read-only view — replies are disabled while viewing as another user.
              </div>
            ) : (
              <ReplyComposer ticketId={ticketId} status={data.ticket.status} viewer={data.viewerRelation} />
            )}
          </div>
        </div>
        <aside className="w-72 shrink-0 border-l border-border flex flex-col bg-background overflow-y-auto">
          <TrackerPanel ticket={data.ticket} viewerRelation={data.viewerRelation} />
        </aside>
      </div>
    </div>
  )
}
